import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { Loader2, Sparkles, Check, Trash2, Send, Mail, Users, Clapperboard, Archive, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useHub, olderThan, matchesSearch } from "@/context/contentHub";

const CTA_LABELS = { study: "Study in OpenWord AI", blog: "Read full study (blog)", signup: "Start free" };
const STATUS_STYLES = {
  draft: "bg-[#141B29] text-slate-400",
  approved: "bg-[#10222A] text-[#00E5FF]",
  sent: "bg-emerald-500/10 text-emerald-400",
};

export default function NuggetsAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [showArchive, setShowArchive] = useState(false);
  const { search: hubSearch = "", filter: hubFilter = "all", archiveDays = 7, reportSummary } = useHub();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/nuggets");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load nuggets");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!reportSummary || !data) return;
    const list = data?.nuggets || [];
    const c = {};
    list.forEach((n) => { c[n.status] = (c[n.status] || 0) + 1; });
    const dots = [];
    if (c.draft) dots.push({ label: `${c.draft} draft`, tone: "amber" });
    if (c.approved) dots.push({ label: `${c.approved} approved`, tone: "blue" });
    if (c.sent) dots.push({ label: `${c.sent} sent`, tone: "green" });
    const needsAction = c.draft || 0;
    reportSummary({ subtitle: `${list.length} total · ${needsAction} to review`, needsAction, dots });
  }, [data, reportSummary]);

  const draftAhead = async () => {
    setDrafting(true);
    try {
      const { data: r } = await api.post("/admin/nuggets/draft?days=7");
      toast.success(r.count ? `Drafted ${r.count} new nugget${r.count > 1 ? "s" : ""}` : "You're already stocked for the week");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draft nuggets");
    }
    setDrafting(false);
  };

  const patch = async (id, body) => {
    setBusyId(id);
    try {
      await api.put(`/admin/nuggets/${id}`, body);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update");
    }
    setBusyId("");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this nugget?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/nuggets/${id}`);
      await load();
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete");
    }
    setBusyId("");
  };

  const testSend = async (id) => {
    setBusyId(id);
    try {
      const { data: r } = await api.post(`/admin/nuggets/${id}/test`);
      toast.success(r.emailed ? `Test sent to ${r.to}` : "Send attempted — check the sending domain");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send test");
    }
    setBusyId("");
  };

  const makeReel = async (n) => {
    setBusyId(n.id);
    try {
      const brief = `${n.reference}: "${n.verse}" — ${n.catch || ""}`.trim();
      await api.post("/admin/reels", { brief });
      toast.success("Turning this nugget into a Reel — see the Reels tab (~1-3 min).");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the reel");
    }
    setBusyId("");
  };

  const sendNow = async () => {
    if (!window.confirm("Send today's approved nugget to all active subscribers now?")) return;
    try {
      const { data: r } = await api.post("/admin/nuggets/send-now");
      toast.success(r.reason ? r.reason : `Sent to ${r.sent} subscriber${r.sent === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send");
    }
  };

  if (loading) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} />
      </div>
    );
  }

  const nuggets = data?.nuggets || [];
  const NEEDS = ["draft"];
  const nMatch = (n) =>
    matchesSearch(`${n.reference} ${n.verse} ${n.catch} ${n.question}`, hubSearch) &&
    (hubFilter === "all" ||
      (hubFilter === "action" && n.status === "draft") ||
      (hubFilter === "scheduled" && n.status === "approved") ||
      (hubFilter === "posted" && n.status === "sent"));
  const isArch = (n) => !NEEDS.includes(n.status) && olderThan(n.scheduled_date, archiveDays);
  const shownN = nuggets.filter(nMatch);
  const activeN = shownN.filter((n) => !isArch(n));
  const archivedN = shownN.filter(isArch);

  return (
    <div className="space-y-5" data-testid="nuggets-admin">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#10222A] text-[#00E5FF] px-3 py-1.5 font-medium" data-testid="nugget-subscriber-count">
            <Users className="w-4 h-4" strokeWidth={1.5} /> {data?.subscriber_count ?? 0} subscribers
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="nugget-draft-week"
            onClick={draftAhead}
            disabled={drafting}
            className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium px-4 py-2 transition-colors disabled:opacity-60"
          >
            {drafting ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
            Draft next 7 days
          </button>
          <button
            data-testid="nugget-send-now"
            onClick={sendNow}
            className="inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#10222A] text-sm font-medium px-4 py-2 transition-colors"
          >
            <Send className="w-4 h-4" strokeWidth={1.5} /> Send today&rsquo;s now
          </button>
        </div>
      </div>

      <p className="text-sm text-slate-400 leading-relaxed">
        Nuggets are AI-drafted a week ahead. Review and edit the reflection, choose a CTA, then <strong>Approve</strong> —
        only approved nuggets send. The daily send goes out at 12:00 UTC to all active subscribers.
      </p>

      {shownN.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#29344D] py-12 text-center text-sm text-slate-500" data-testid="nuggets-empty">
          {nuggets.length === 0
            ? "No nuggets yet. Tap “Draft next 7 days” to generate a week of drafts to review."
            : "Nothing matches your search or filter."}
        </div>
      ) : (
        <div className="space-y-4">
          {activeN.map((n) => (
            <div key={n.id} data-testid={`nugget-${n.id}`} className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-5 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-200">{n.scheduled_date}</span>
                  <span className="text-xs font-medium uppercase tracking-widest text-[#00E5FF]">{n.reference}</span>
                </div>
                <span
                  data-testid={`nugget-status-${n.id}`}
                  className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLES[n.status] || STATUS_STYLES.draft}`}
                >
                  {n.status}{n.status === "sent" && n.sent_count ? ` · ${n.sent_count}` : ""}
                </span>
              </div>

              {n.verse && <p className="text-lg text-slate-300 leading-relaxed">&ldquo;{n.verse}&rdquo;</p>}

              <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">The catch</label>
              <textarea
                data-testid={`nugget-catch-${n.id}`}
                defaultValue={n.catch}
                onBlur={(e) => { if (e.target.value.trim() !== n.catch) patch(n.id, { catch: e.target.value.trim() }); }}
                rows={2}
                placeholder="1-2 sentences pointing at a specific detail in the text (no life-application)"
                className="w-full rounded-xl border border-[#29344D] bg-[#0B0F16] p-3 text-sm text-slate-300 leading-relaxed focus:outline-none focus:border-[#00E5FF]/50"
              />
              <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mt-1">The question <span className="text-slate-500 normal-case">(unresolved)</span></label>
              <textarea
                data-testid={`nugget-question-${n.id}`}
                defaultValue={n.question}
                onBlur={(e) => { if (e.target.value.trim() !== n.question) patch(n.id, { question: e.target.value.trim() }); }}
                rows={2}
                placeholder="One question that sends the reader back into the passage"
                className="w-full rounded-xl border border-[#29344D] bg-[#0B0F16] p-3 text-sm text-slate-200 font-medium leading-relaxed focus:outline-none focus:border-[#00E5FF]/50"
              />

              <div className="flex items-center gap-2 flex-wrap">
                <select
                  data-testid={`nugget-cta-${n.id}`}
                  value={n.cta_type}
                  onChange={(e) => patch(n.id, { cta_type: e.target.value })}
                  className="h-9 rounded-lg border border-[#29344D] bg-[#0E131F] px-2.5 text-sm text-slate-400 focus:outline-none"
                >
                  {Object.entries(CTA_LABELS).map(([v, l]) => <option key={v} value={v}>{`CTA: ${l}`}</option>)}
                </select>

                {n.status !== "approved" && n.status !== "sent" && (
                  <button
                    data-testid={`nugget-approve-${n.id}`}
                    onClick={() => patch(n.id, { status: "approved" })}
                    disabled={busyId === n.id}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm px-3.5 py-1.5 transition-colors disabled:opacity-60"
                  >
                    <Check className="w-4 h-4" strokeWidth={1.5} /> Approve
                  </button>
                )}
                {n.status === "approved" && (
                  <button
                    data-testid={`nugget-unapprove-${n.id}`}
                    onClick={() => patch(n.id, { status: "draft" })}
                    disabled={busyId === n.id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#29344D] text-slate-400 hover:bg-[#0B0F16] text-sm px-3.5 py-1.5 transition-colors"
                  >
                    Un-approve
                  </button>
                )}

                <button
                  data-testid={`nugget-test-${n.id}`}
                  onClick={() => testSend(n.id)}
                  disabled={busyId === n.id}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#29344D] text-slate-400 hover:bg-[#0B0F16] text-sm px-3.5 py-1.5 transition-colors"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.5} /> Test to me
                </button>

                <button
                  data-testid={`nugget-reel-${n.id}`}
                  onClick={() => makeReel(n)}
                  disabled={busyId === n.id}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#29344D] text-slate-400 hover:bg-[#0B0F16] text-sm px-3.5 py-1.5 transition-colors"
                >
                  <Clapperboard className="w-4 h-4" strokeWidth={1.5} /> Make Reel
                </button>

                <button
                  data-testid={`nugget-delete-${n.id}`}
                  onClick={() => remove(n.id)}
                  disabled={busyId === n.id}
                  className="ml-auto inline-flex items-center justify-center w-9 h-9 rounded-full text-slate-500 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                  aria-label="Delete nugget"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
          {archivedN.length > 0 && (
            <div className="border border-dashed border-[#29344D] rounded-2xl bg-[#0B0F16]/40" data-testid="nugget-archive">
              <button data-testid="nugget-archive-toggle" onClick={() => setShowArchive((v) => !v)}
                className="w-full flex items-center gap-3 p-4 text-left">
                <span className="w-8 h-8 rounded-lg bg-[#141B29] text-slate-500 flex items-center justify-center shrink-0"><Archive className="w-4 h-4" strokeWidth={1.7} /></span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-400">Archived · older than 7 days</span>
                  <span className="block text-xs text-slate-500">{archivedN.length} sent — hidden to reduce clutter, still here to reuse</span>
                </span>
                <ChevronRight className={`ml-auto w-4 h-4 text-slate-500 transition-transform ${showArchive ? "rotate-90" : ""}`} />
              </button>
              {showArchive && (
                <div className="border-t border-dashed border-[#29344D] p-2 space-y-1">
                  {archivedN.map((n) => (
                    <div key={n.id} data-testid={`nugget-arch-${n.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#0E131F] transition-colors">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-300 truncate">{n.reference} · <span className="text-slate-500 font-normal">{n.scheduled_date}</span></div>
                        {n.verse && <div className="text-xs text-slate-500 truncate">{n.verse}</div>}
                      </div>
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${STATUS_STYLES[n.status] || STATUS_STYLES.draft}`}>{n.status}{n.status === "sent" && n.sent_count ? ` · ${n.sent_count}` : ""}</span>
                      <button data-testid={`nugget-arch-reel-${n.id}`} onClick={() => makeReel(n)} disabled={busyId === n.id}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#29344D] text-slate-400 hover:bg-[#0B0F16] text-xs px-3 py-1.5">
                        <Clapperboard className="w-3.5 h-3.5" strokeWidth={1.5} /> Reuse
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
